import { useMemo, useState, type ReactNode } from "react";
import {
  AlertTriangle,
  Briefcase,
  Building2,
  CalendarClock,
  Check,
  CheckCircle2,
  DollarSign,
  Gauge,
  Handshake,
  Link2,
  Percent,
  ShieldCheck,
  Target,
  TrendingUp,
  Wallet,
} from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import {
  SBA_DEFAULTS,
  SBA_MAX_TERM_NO_RE,
  SBA_MIN_DSCR,
  amortizationSchedule,
  computeSba,
  maxPriceForDscr,
  toSbaQuery,
  type CashFlowBasis,
  type SbaInputs,
} from "@/lib/sbaMath";

// Sanity ceiling for dollar inputs — blocks extra-zero typos ($1B is far past
// any SBA-financeable deal) without constraining real ones.
const MAX_DOLLARS = 1_000_000_000;

type NumKey = {
  [K in keyof SbaInputs]: SbaInputs[K] extends number ? K : never;
}[keyof SbaInputs];

interface FieldDef {
  key: NumKey;
  label: string;
  hint: string;
  icon: typeof DollarSign;
  prefix?: string;
  suffix?: string;
  step?: string;
  min: number;
  max?: number;
  /** Whole numbers only (e.g. loan term in years). */
  integer?: boolean;
}

const dollars = (key: NumKey, label: string, hint: string, icon: typeof DollarSign): FieldDef =>
  ({ key, label, hint, icon, prefix: "$", min: 0, max: MAX_DOLLARS, step: "1000" });

const F: Record<NumKey, FieldDef> = {
  askingPrice: dollars("askingPrice", "Asking Price", "Total purchase price of the business", DollarSign),
  closingCostsPct: { key: "closingCostsPct", label: "Closing Costs", hint: "Legal, appraisal, packaging — % of price, financed", icon: Percent, suffix: "%", min: 0, max: 20, step: "0.5" },
  workingCapital: dollars("workingCapital", "Working Capital", "Operating cash financed with the deal", Wallet),
  downPaymentPct: { key: "downPaymentPct", label: "Buyer Cash Down", hint: "Your cash at closing — % of total project cost", icon: Percent, suffix: "%", min: 0, max: 100, step: "0.5" },
  // SBA 7(a) pricing is prime + a capped spread — nothing legitimate gets near 30%.
  interestRate: { key: "interestRate", label: "SBA Interest Rate", hint: "Annual rate on the SBA loan", icon: Percent, suffix: "%", min: 0, max: 30, step: "0.1" },
  loanTermYears: { key: "loanTermYears", label: "SBA Loan Term", hint: `Up to ${SBA_MAX_TERM_NO_RE} yrs for a business; 25 with real estate`, icon: CalendarClock, suffix: "yrs", min: 1, max: 25, step: "1", integer: true },
  sellerNotePct: { key: "sellerNotePct", label: "Seller Note", hint: "Seller-financed portion — % of price", icon: Handshake, suffix: "%", min: 0, max: 100, step: "0.5" },
  sellerNoteRate: { key: "sellerNoteRate", label: "Seller Note Rate", hint: "Annual rate on the seller note", icon: Percent, suffix: "%", min: 0, max: 30, step: "0.1" },
  sellerNoteTermYears: { key: "sellerNoteTermYears", label: "Seller Note Term", hint: "Amortization once payments start", icon: CalendarClock, suffix: "yrs", min: 1, max: 25, step: "1", integer: true },
  cashFlow: dollars("cashFlow", "Cash Flow", "", TrendingUp),
  ownerSalary: dollars("ownerSalary", "Owner / Manager Salary", "Market pay for whoever runs it — lenders deduct this from SDE", Briefcase),
  otherDebtPayments: dollars("otherDebtPayments", "Other Annual Debt Payments", "Yearly payments on debt that stays after closing (seller debt is usually paid off at close)", Wallet),
};

const NUM_KEYS = Object.keys(F) as NumKey[];

type Flags = Pick<SbaInputs, "sellerNoteStandby" | "includesRealEstate" | "cashFlowBasis">;

const usd = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

const usd2 = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(n);

type Tone = "excellent" | "good" | "ok" | "risk" | "neutral";

function riskRating(dscr: number | null): { label: string; tone: Tone } {
  if (dscr === null) return { label: "No debt", tone: "neutral" };
  if (dscr >= 2.0) return { label: "Excellent", tone: "excellent" };
  if (dscr >= 1.5) return { label: "Good", tone: "good" };
  if (dscr >= SBA_MIN_DSCR) return { label: "Acceptable", tone: "ok" };
  return { label: "High Risk", tone: "risk" };
}

// Translucent status hues so they read on both the light and dark theme.
const toneClasses: Record<Tone, string> = {
  excellent: "border-emerald-500/30 bg-emerald-500/10 text-emerald-500",
  good: "border-sky-500/30 bg-sky-500/10 text-sky-500",
  ok: "border-amber-500/30 bg-amber-500/10 text-amber-500",
  risk: "border-rose-500/30 bg-rose-500/10 text-rose-500",
  neutral: "border-border/60 bg-card/70 text-foreground",
};

interface SbaCalculatorFormProps {
  /** Pre-fill any of the deal inputs (e.g. from a listing or a shared link). Omitted fields fall back to defaults. */
  initialValues?: Partial<SbaInputs>;
  /** Show the "Copy link" button that shares this scenario via the URL. */
  shareable?: boolean;
}

export default function SbaCalculatorForm({ initialValues, shareable }: SbaCalculatorFormProps) {
  const [values, setValues] = useState<Record<NumKey, string>>(() => {
    const merged = { ...SBA_DEFAULTS, ...initialValues };
    return Object.fromEntries(NUM_KEYS.map((k) => [k, String(merged[k])])) as Record<NumKey, string>;
  });
  const [flags, setFlags] = useState<Flags>(() => ({
    sellerNoteStandby: initialValues?.sellerNoteStandby ?? SBA_DEFAULTS.sellerNoteStandby,
    includesRealEstate: initialValues?.includesRealEstate ?? SBA_DEFAULTS.includesRealEstate,
    cashFlowBasis: initialValues?.cashFlowBasis ?? SBA_DEFAULTS.cashFlowBasis,
  }));
  const [copied, setCopied] = useState(false);

  const setField = (key: NumKey, raw: string) => setValues((v) => ({ ...v, [key]: raw }));
  const setFlag = <K extends keyof Flags>(key: K, value: Flags[K]) => setFlags((f) => ({ ...f, [key]: value }));

  // Validation — produce a per-field error map.
  const errors = useMemo(() => {
    const e: Partial<Record<NumKey, string>> = {};
    for (const f of Object.values(F)) {
      const raw = values[f.key].trim();
      if (raw === "") {
        e[f.key] = "Required";
        continue;
      }
      const n = Number(raw);
      if (!Number.isFinite(n)) {
        e[f.key] = "Enter a number";
        continue;
      }
      if (n < f.min) e[f.key] = `Must be ≥ ${f.min.toLocaleString()}`;
      else if (f.max !== undefined && n > f.max) e[f.key] = `Must be ≤ ${f.max.toLocaleString()}`;
      else if (f.integer && !Number.isInteger(n)) e[f.key] = "Whole numbers only";
    }
    if (!e.askingPrice && Number(values.askingPrice) <= 0) e.askingPrice = "Must be greater than 0";
    // Buyer cash + seller note can't fund more than the project (price ≤ project cost, so this bound is safe).
    if (!e.downPaymentPct && !e.sellerNotePct && Number(values.downPaymentPct) + Number(values.sellerNotePct) > 100)
      e.sellerNotePct = "Cash down + seller note can't exceed 100%";
    return e;
  }, [values]);

  const isValid = Object.keys(errors).length === 0;

  const inputs = useMemo<SbaInputs | null>(() => {
    if (!isValid) return null;
    const nums = Object.fromEntries(NUM_KEYS.map((k) => [k, Number(values[k])])) as Record<NumKey, number>;
    return { ...nums, ...flags };
  }, [isValid, values, flags]);

  const result = useMemo(() => (inputs ? computeSba(inputs) : null), [inputs]);
  const maxPrice = useMemo(() => (inputs ? maxPriceForDscr(inputs) : null), [inputs]);
  const schedule = useMemo(
    () => (inputs && result && result.loanAmount > 0 ? amortizationSchedule(result.loanAmount, inputs.interestRate, inputs.loanTermYears) : []),
    [inputs, result]
  );

  const risk = result ? riskRating(result.dscr) : null;
  const dscrText = result ? (result.dscr === null ? "n/a" : result.dscr.toFixed(2) + "x") : "—";
  const isSde = flags.cashFlowBasis === "sde";
  const hasNote = Number(values.sellerNotePct) > 0;

  const copyLink = async () => {
    if (!inputs) return;
    const url = `${window.location.origin}${window.location.pathname}?${toSbaQuery(inputs)}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt("Copy this link:", url);
    }
  };

  const renderField = (f: FieldDef, overrides?: Partial<Pick<FieldDef, "label" | "hint">>) => {
    const Icon = f.icon;
    const err = errors[f.key];
    return (
      <div key={f.key}>
        <Label htmlFor={f.key} className="mb-1.5 flex items-center gap-1.5 text-sm font-semibold text-foreground">
          <Icon className="h-3.5 w-3.5 text-muted-foreground" />
          {overrides?.label ?? f.label}
        </Label>
        <div className="relative">
          {f.prefix && (
            <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-sm font-medium text-muted-foreground">
              {f.prefix}
            </span>
          )}
          <Input
            id={f.key}
            type="number"
            inputMode="decimal"
            step={f.step}
            min={f.min}
            max={f.max}
            value={values[f.key]}
            onChange={(e) => setField(f.key, e.target.value)}
            className={cn(f.prefix && "pl-8", f.suffix && "pr-12", err && "border-rose-400/70 focus-visible:ring-rose-300/40")}
            aria-invalid={!!err}
          />
          {f.suffix && (
            <span className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-sm font-medium text-muted-foreground">
              {f.suffix}
            </span>
          )}
        </div>
        <p className={cn("mt-1 text-xs", err ? "text-rose-500" : "text-muted-foreground")}>{err ?? overrides?.hint ?? f.hint}</p>
      </div>
    );
  };

  const bannerTone = !result ? null : result.status === "eligible" ? "excellent" : result.status === "review" ? "ok" : "neutral";

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]">
      {/* ── Inputs ─────────────────────────────────────────────── */}
      <Card className="rounded-3xl">
        <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
          <CardTitle className="text-lg">Deal inputs</CardTitle>
          {shareable && (
            <button
              type="button"
              onClick={copyLink}
              disabled={!inputs}
              className="inline-flex items-center gap-1.5 rounded-full border border-primary/25 bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary transition hover:bg-primary/15 disabled:opacity-50"
            >
              {copied ? <Check className="h-3.5 w-3.5" /> : <Link2 className="h-3.5 w-3.5" />}
              {copied ? "Link copied" : "Copy link"}
            </button>
          )}
        </CardHeader>
        <CardContent className="space-y-6">
          <Section title="Purchase">
            {renderField(F.askingPrice)}
            {renderField(F.closingCostsPct)}
            {renderField(F.workingCapital)}
          </Section>

          <Section title="SBA loan">
            {renderField(F.downPaymentPct)}
            {renderField(F.interestRate)}
            {renderField(F.loanTermYears)}
            <Toggle
              id="includesRealEstate"
              checked={flags.includesRealEstate}
              onChange={(v) => setFlag("includesRealEstate", v)}
              icon={Building2}
              label="Deal includes real estate"
              hint="Allows a term up to 25 years"
            />
          </Section>

          <Section title="Seller financing">
            {renderField(F.sellerNotePct)}
            {hasNote && (
              <>
                {renderField(F.sellerNoteRate)}
                {renderField(F.sellerNoteTermYears)}
                <Toggle
                  id="sellerNoteStandby"
                  checked={flags.sellerNoteStandby}
                  onChange={(v) => setFlag("sellerNoteStandby", v)}
                  icon={Handshake}
                  label="Note on full standby"
                  hint="No payments while the SBA loan is outstanding — can count toward up to half the 10% equity injection, and stays out of DSCR"
                />
              </>
            )}
          </Section>

          <Section title="Cash flow">
            <div>
              <div className="mb-1.5 text-sm font-semibold text-foreground">Cash flow basis</div>
              <div className="inline-flex rounded-full border border-border/60 bg-card/70 p-1">
                {(["sde", "ebitda"] as CashFlowBasis[]).map((b) => (
                  <button
                    key={b}
                    type="button"
                    onClick={() => setFlag("cashFlowBasis", b)}
                    className={cn(
                      "rounded-full px-3.5 py-1 text-xs font-semibold transition",
                      flags.cashFlowBasis === b ? "bg-gold-gradient text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                    )}
                  >
                    {b === "sde" ? "SDE" : "EBITDA"}
                  </button>
                ))}
              </div>
            </div>
            {renderField(F.cashFlow, {
              label: isSde ? "Seller's Discretionary Earnings" : "EBITDA",
              hint: isSde ? "Annual SDE — includes the owner's own pay" : "Annual EBITDA — owner/manager pay already deducted",
            })}
            {isSde && renderField(F.ownerSalary)}
            {renderField(F.otherDebtPayments)}
          </Section>
        </CardContent>
      </Card>

      {/* ── Results ────────────────────────────────────────────── */}
      <div className="space-y-6">
        {/* SBA eligibility banner */}
        <div className={cn("flex items-start gap-3 rounded-3xl border p-5", bannerTone ? toneClasses[bannerTone] : "border-border/60 bg-card/60")}>
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-current/10">
            {result?.status === "eligible" ? (
              <CheckCircle2 className="h-5 w-5" />
            ) : result?.status === "review" ? (
              <AlertTriangle className="h-5 w-5" />
            ) : (
              <ShieldCheck className="h-5 w-5 text-muted-foreground" />
            )}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-bold text-foreground">
              {!result
                ? "SBA eligibility"
                : result.status === "eligible"
                  ? "Likely SBA 7(a) eligible"
                  : result.status === "review"
                    ? "Review needed for SBA 7(a)"
                    : "No SBA loan in this scenario"}
            </div>
            <p className="mt-0.5 text-xs leading-5 text-muted-foreground">
              {!result
                ? "Fix the highlighted inputs to see eligibility."
                : result.status === "eligible"
                  ? "Meets the common 7(a) thresholds for loan size, equity injection, term, and DSCR."
                  : result.reasons.join(" · ")}
            </p>
          </div>
        </div>

        {/* Metric dashboard cards */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <Metric label="SBA Loan" value={result ? usd(result.loanAmount) : "—"} icon={DollarSign} />
          <Metric label="Monthly Payment" value={result ? usd2(result.monthlyPayment) : "—"} icon={CalendarClock} />
          <Metric label="Total Debt Service / yr" value={result ? usd(result.totalAnnualDebtService) : "—"} icon={Wallet} />
          <Metric label="Buyer Cash Required" value={result ? usd(result.buyerCashRequired) : "—"} icon={DollarSign} />
          <Metric label="DSCR" value={dscrText} icon={Gauge} highlight={risk ? toneClasses[risk.tone] : undefined} />
          <Metric label="Risk Rating" value={risk ? risk.label : "—"} icon={ShieldCheck} highlight={risk ? toneClasses[risk.tone] : undefined} />
        </div>

        {/* Price that clears the lender bar */}
        {result && result.loanAmount > 0 && (
          <div className="flex items-start gap-3 rounded-3xl border border-primary/25 bg-primary/10 p-5">
            <Target className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
            <div className="min-w-0 text-sm">
              <div className="font-bold text-foreground">
                {maxPrice !== null
                  ? `Max price at ${SBA_MIN_DSCR.toFixed(2)}x DSCR: ${usd(maxPrice)}`
                  : `No price clears ${SBA_MIN_DSCR.toFixed(2)}x DSCR`}
              </div>
              <p className="mt-0.5 text-xs leading-5 text-muted-foreground">
                {maxPrice === null
                  ? "Adjusted cash flow can't cover the other debt payments at the lender minimum."
                  : maxPrice >= result.askingPrice
                    ? `${usd(maxPrice - result.askingPrice)} of headroom over the asking price, holding your other terms.`
                    : `${usd(result.askingPrice - maxPrice)} below asking — negotiate price, add a seller note, or put more cash down.`}
              </p>
            </div>
          </div>
        )}

        {/* Detailed breakdown */}
        <Card className="rounded-3xl">
          <CardHeader>
            <CardTitle className="text-lg">Calculation breakdown</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <BreakdownGroup title="Uses">
              <BreakdownRow label="Asking Price" value={result ? usd(result.askingPrice) : "—"} />
              <BreakdownRow label="Closing Costs" value={result ? usd(result.closingCosts) : "—"} />
              <BreakdownRow label="Working Capital" value={result ? usd(result.workingCapital) : "—"} />
              <BreakdownRow label="Total Project Cost" value={result ? usd(result.totalProjectCost) : "—"} strong />
            </BreakdownGroup>

            <BreakdownGroup title="Sources">
              <BreakdownRow label="Buyer Cash Down" value={result ? usd(result.downPayment) : "—"} />
              <BreakdownRow label="Seller Note" value={result ? usd(result.sellerNote) : "—"} />
              <BreakdownRow label="SBA Loan (before fee)" value={result ? usd(result.baseLoan) : "—"} />
              <BreakdownRow
                label={result && result.guaranteedPct ? `SBA Guaranty Fee  (on ${result.guaranteedPct}% guaranteed, financed)` : "SBA Guaranty Fee"}
                value={result ? usd(result.guarantyFee) : "—"}
              />
              <BreakdownRow label="Total SBA Loan" value={result ? usd(result.loanAmount) : "—"} strong />
              <BreakdownRow
                label="Equity Injection  (cash + eligible standby note)"
                value={result ? `${usd(result.equityCounted)}  ·  ${result.equityPct.toFixed(1)}%` : "—"}
              />
              {result && result.downPayment < result.minBuyerCash && (
                <BreakdownRow label="Min. Buyer Cash for 10% Injection" value={usd(result.minBuyerCash)} />
              )}
            </BreakdownGroup>

            <BreakdownGroup title="Debt service">
              <BreakdownRow label="SBA Annual Debt Service  (Monthly × 12)" value={result ? usd(result.annualDebtService) : "—"} />
              <BreakdownRow
                label={flags.sellerNoteStandby && hasNote ? "Seller Note Payments  (on standby — deferred)" : "Seller Note Payments"}
                value={result ? usd(result.sellerNoteAnnualInDscr) : "—"}
              />
              <BreakdownRow label="Other Debt Payments" value={result ? usd(result.otherDebtPayments) : "—"} />
              <BreakdownRow label="Total Annual Debt Service" value={result ? usd(result.totalAnnualDebtService) : "—"} strong />
            </BreakdownGroup>

            <BreakdownGroup title="Coverage">
              <BreakdownRow label={isSde ? "SDE" : "EBITDA"} value={inputs ? usd(inputs.cashFlow) : "—"} />
              {isSde && <BreakdownRow label="− Owner / Manager Salary" value={inputs ? usd(inputs.ownerSalary) : "—"} />}
              <BreakdownRow label="Adjusted Cash Flow" value={result ? usd(result.adjustedCashFlow) : "—"} />
              <BreakdownRow label="Adjusted Cash Flow ÷ Total Debt Service  =  DSCR" value={dscrText} strong />
            </BreakdownGroup>
          </CardContent>
        </Card>

        {/* Amortization schedule */}
        {schedule.length > 0 && (
          <Card className="rounded-3xl">
            <details>
              <summary className="cursor-pointer select-none px-6 py-5 text-lg font-semibold text-foreground">
                SBA loan amortization schedule
              </summary>
              <div className="overflow-x-auto px-6 pb-6">
                <table className="w-full min-w-[30rem] text-sm tabular-nums">
                  <thead>
                    <tr className="border-b border-border/60 text-left text-[0.7rem] uppercase tracking-wide text-muted-foreground">
                      <th className="py-2 pr-3 font-semibold">Year</th>
                      <th className="py-2 pr-3 text-right font-semibold">Payments</th>
                      <th className="py-2 pr-3 text-right font-semibold">Interest</th>
                      <th className="py-2 pr-3 text-right font-semibold">Principal</th>
                      <th className="py-2 text-right font-semibold">Ending Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {schedule.map((r) => (
                      <tr key={r.year} className="border-b border-border/40 last:border-0">
                        <td className="py-2 pr-3 text-muted-foreground">{r.year}</td>
                        <td className="py-2 pr-3 text-right text-foreground">{usd(r.payment)}</td>
                        <td className="py-2 pr-3 text-right text-foreground">{usd(r.interest)}</td>
                        <td className="py-2 pr-3 text-right text-foreground">{usd(r.principal)}</td>
                        <td className="py-2 text-right font-semibold text-foreground">{usd(r.endingBalance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          </Card>
        )}

        <p className="px-1 text-[0.7rem] leading-5 text-muted-foreground">
          Estimates only. The guaranty fee uses the standard SBA schedule, which the SBA adjusts or waives by fiscal year;
          rates, terms, and eligibility are set by lenders and the SBA. Confirm figures with a qualified lender before
          relying on them.
        </p>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="space-y-4">
      <div className="text-[0.7rem] font-semibold uppercase tracking-wide text-primary">{title}</div>
      {children}
    </div>
  );
}

function Toggle({
  id,
  checked,
  onChange,
  icon: Icon,
  label,
  hint,
}: {
  id: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  icon: typeof DollarSign;
  label: string;
  hint: string;
}) {
  return (
    <label htmlFor={id} className="flex cursor-pointer items-start gap-3">
      <input id={id} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-1 h-4 w-4 accent-primary" />
      <span className="min-w-0">
        <span className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
          <Icon className="h-3.5 w-3.5 text-muted-foreground" />
          {label}
        </span>
        <span className="mt-0.5 block text-xs text-muted-foreground">{hint}</span>
      </span>
    </label>
  );
}

function Metric({
  label,
  value,
  icon: Icon,
  highlight,
}: {
  label: string;
  value: string;
  icon: typeof DollarSign;
  highlight?: string;
}) {
  return (
    <div className={cn("flex flex-col justify-between rounded-2xl border p-4", highlight ?? "border-border/60 bg-card/70")}>
      <div className={cn("flex items-center gap-1.5 text-[0.7rem] font-semibold uppercase tracking-wide", highlight ? "opacity-80" : "text-muted-foreground")}>
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <div className={cn("mt-2 text-lg font-extrabold leading-tight", !highlight && "text-foreground")}>{value}</div>
    </div>
  );
}

function BreakdownGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <div className="mb-1 text-[0.7rem] font-semibold uppercase tracking-wide text-muted-foreground">{title}</div>
      {children}
    </div>
  );
}

function BreakdownRow({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-border/40 py-2.5 last:border-0">
      <span className={cn("text-sm", strong ? "font-bold text-foreground" : "text-muted-foreground")}>{label}</span>
      <span className={cn("shrink-0 tabular-nums", strong ? "text-base font-extrabold text-primary" : "text-sm font-semibold text-foreground")}>
        {value}
      </span>
    </div>
  );
}
